import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { TopHeader } from 'ui/components/panes/Header/TopHeader';
import { TopFooter } from 'ui/components/panes/Footer/TopFooter';
import { KvPictures } from 'ui/components/public/patterns/Top/KvPictures';
import { KvMainText } from 'ui/components/public/patterns/Top/KvMainText';
import { Announcements } from 'ui/components/public/patterns/Top/Announcements';
import { MaForBeginner } from 'ui/components/public/patterns/Top/MaForBeginner';
import { LeadSection } from 'ui/components/public/patterns/Top/LeadSection';
import { Movies } from 'ui/components/public/patterns/Top/Movies';
import { QA } from 'ui/components/public/patterns/Top/QA';
import { Offer } from 'ui/components/public/patterns/Top/Offer';
import KvPcImg from 'ui/images/top/kv_building_pc.svg';
import KvSpImg from 'ui/images/top/kv_bg_sp.svg';
import { Only } from 'ui/components/elements/Only/Only';
import { mqMin } from 'utils/responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { AboutService } from 'ui/components/public/patterns/Top/AboutService';
import { AboutServiceDetail } from 'ui/components/public/patterns/Top/AboutServiceDetail';

const Wrapper = styled.div`
    font-weight: 700;

    @keyframes kvFadein {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
    @keyframes kvFadeinY {
        0% {
            opacity: 0;
            translate: -50% 25px;
        }
        100% {
            opacity: 1;
            translate: -50% 0;
        }
    }
    @keyframes kvFadeinZoom {
        0% {
            opacity: 0;
            scale: 0.9;
        }
        100% {
            opacity: 1;
            scale: 1;
        }
    }
`;

const MainSection = styled.section`
    position: relative;
    width: 100%;
    background-color: #55b6e6;
    padding-bottom: 31rem;
    overflow: hidden;

    ${mqMin('NARROW')} {
        width: 100%;
        padding-top: 20px;
        padding-bottom: 7rem;
        overflow: hidden;
    }

    img {
        display: block;
        max-width: 100%;
    }
`;

const BgImg = styled.img`
    width: 100%;
    position: absolute;
    top: 9rem;

    ${mqMin('NARROW')} {
        max-width: 1511px;
        bottom: -90px;
        top: auto;
        transform: none;
    }
`;

const ValuationButton = styled.button`
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background-color: #fff;
    border: 3px solid var(--main-color-blue);
    color: var(--main-color-blue);
    font-size: 14px;
    font-weight: 900;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    line-height: 1.3;
    text-align: center;
    padding: 8px;

    &:hover {
        transform: translateY(-2px) scale(1.05);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        background-color: var(--main-color-blue);
        color: #fff;
    }

    &:active {
        transform: translateY(0) scale(0.98);
        transition: all 0.1s ease;
    }

    ${mqMin('SP')} {
        bottom: 32px;
        right: 40px;
        width: 140px;
        height: 140px;
        font-size: 18px;
    }
`;

export const Top: React.FC = () => {
    const ticking = useRef(false);
    const fadeItems = useRef<NodeListOf<Element> | null>(null);
    const fadeItemsLi = useRef<NodeListOf<Element> | null>(null);
    const location = useLocation();
    const navigate = useNavigate();

    const handleValuationClick = () => {
        navigate('/valuation/estimate/');
    };

    useEffect(() => {
        // topページにのみ適用するcssを追加
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = './top.css';
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, []);

    useEffect(() => {
        // フェードイン処理
        fadeItems.current = document.querySelectorAll('.fadein');
        fadeItemsLi.current = document.querySelectorAll('.fadeinLi');

        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const fadePoint = 350;

            fadeItems.current?.forEach((e) => {
                const itemTop = e.getBoundingClientRect().top + scrollTop;

                if (
                    scrollTop + windowHeight - fadePoint > itemTop ||
                    scrollTop + windowHeight >= documentHeight - 100
                ) {
                    e.classList.add('scroll_on');
                }
            });

            fadeItemsLi.current?.forEach((e) => {
                const itemTop = e.getBoundingClientRect().top + scrollTop;
                const childfadeItems = Array.from(e.children);
                if (scrollTop + fadePoint > itemTop) {
                    childfadeItems.forEach((elm, i) => {
                        setTimeout(() => {
                            elm.classList.add('scroll_on');
                        }, 300 * i);
                    });
                }
            });

            ticking.current = false;
        };

        function onScroll() {
            if (!ticking.current) {
                requestAnimationFrame(handleScroll);
                ticking.current = true;
            }
        }

        window.addEventListener('scroll', onScroll);
        handleScroll();

        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    useEffect(() => {
        if (location.hash) {
            const targetId = location.hash.replace('#', '');
            const el = document.getElementById(targetId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [location.hash]);

    return (
        <Wrapper>
            <TopHeader />
            <MainSection>
                <Only sp>
                    <BgImg src={KvSpImg} alt="背景画像" />
                </Only>
                <Only pc>
                    <BgImg src={KvPcImg} alt="背景画像" />
                </Only>
                <KvPictures />
                <KvMainText />
            </MainSection>

            <main id="home">
                <Movies backgroundColor="var(--lightblue)" title="MOVIE" />
                <Announcements />
                <LeadSection />
                <AboutService showTitle={false} />
                <MaForBeginner />
                <AboutServiceDetail />
                <QA />
                <Offer />
            </main>
            <TopFooter />
            <ValuationButton onClick={handleValuationClick}>
                無料で <br />
                企業価値 <br />
                算出
            </ValuationButton>
        </Wrapper>
    );
};
